<template>
  <div class="pay-container" data-cy="pay-in-person-screen">
    <v-btn
      v-if="showResetButton"
      class="close-button"
      dense
      text
      icon
      @click="startNewSession"
    >
      <v-progress-circular
        v-if="resetting"
        color="black"
        size="20"
        width="2"
        indeterminate
      />
      <v-icon v-else color="black" dense>mdi-replay</v-icon>
    </v-btn>
    <div v-if="loading">
      <v-progress-circular indeterminate />
    </div>
    <div v-else class="pay-content-container">
      <div class="pay-card">
        <div class="icon-container">
          <div class="icon-content-container">
            <v-img src="/assets/icons/bill.svg" max-width="50px" />
          </div>
        </div>
        <div class="my-10 text-center">
          <InPersonPaymentStatus
            ref="status"
            :dialog-text="dialogText"
            @abort="handleAbortTransaction"
          />
        </div>
      </div>
      <InPersonPaymentErrorBottomSheet
        ref="errorBottomSheet"
        @retry="handleRetry"
        @startOver="startNewSession"
      />
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue';
import InPersonPaymentStatus from '@/components/Payments/InPersonPaymentStatus.vue';
import InPersonPaymentErrorBottomSheet from '@/components/Payments/InPersonPaymentErrorBottomSheet.vue';
import { OPERATION, generateCRN } from '@/utils/worldLine';
import { getKioskOrderLink } from '@/utils/orders';

const PAYMENT_STATUSES = {
  idle: 'idle',
  processing: 'processing',
  error: 'error',
  success: 'success',
  disconnected: 'disconnected',
  void: 'void',
};

const ORDER_SYNC_INTERVAL_MS = 1000;
// card interaction (insert card, PIN entry) can legitimately take a while
const SALE_WATCHDOG_MS = 120 * 1000;
// after the terminal approved the sale we only wait on our own backend
const COMPLETING_ORDER_WATCHDOG_MS = 60 * 1000;
const ABORT_RESET_BUTTON_MS = 15 * 1000;

export default {
  components: {
    InPersonPaymentStatus,
    InPersonPaymentErrorBottomSheet,
  },
  data() {
    return {
      loading: false,
      posStatus: PAYMENT_STATUSES.idle,
      connection: null,
      error: null,
      paymentIntent: {},
      dialogText: null,
      uniqueOrderNumber: null,
      printer: null,
      cardholderReceipt: null,
      printerInterval: null,
      abortedByUser: false,
      resetting: false,
      showResetButton: false,
      terminalOfflineTimeoutId: null,
      hasRetriedNP: false,
      orderSyncIntervalId: null,
      isSyncingOrders: false,
      initialized: false,
      processingWatchdogId: null,
      abortResetTimeoutId: null,
    };
  },
  computed: {
    terminalOnline() {
      return this.$store.getters.terminalOnline;
    },
    printerOnline() {
      return this.$store.getters.printerIsOnline;
    },
    payButtonDisabled() {
      return (
        !this.printerOnline ||
        // !this.terminalOnline ||
        this.posStatus === PAYMENT_STATUSES.void ||
        this.posStatus === PAYMENT_STATUSES.processing
      );
    },
    order() {
      const { getOrderByUniqueOrderNumber } = this.$store.getters;
      return getOrderByUniqueOrderNumber(
        this.paymentIntent?.unique_order_number
      );
    },
    unresolvedCRNs() {
      return this.$store.getters.unresolvedCRNs;
    },
    formattedTotalAmount() {
      return this.$store.getters.getFormattedPrice(this.paymentIntent.total);
    },
  },
  watch: {
    async order(newOrder) {
      if (!newOrder || this.posStatus !== PAYMENT_STATUSES.processing) {
        return;
      }

      const sendingToPOS = newOrder.sending_to_pos;
      const orderSentToPOS = newOrder.sent_to_pos;
      const orderFailed = newOrder.status === 'failed';
      const integratedToPOS = this.$store.getters.posIntegrationEnabled;

      const onCreateOrderSuccess = () => {
        // the money↔order link is settled — only now may the charge leave
        // the auto-void safety net
        this.$store.dispatch('removeUnresolvedCRN', {
          CRN: this.paymentIntent.CRN,
        });
        const sessionToken = this.$route.params.session;
        const QRLink = getKioskOrderLink(
          sessionToken,
          newOrder.unique_order_number,
          { force: true }
        );
        this.printReceipt(
          this.cardholderReceipt,
          this.order.order_id,
          newOrder.additional_fields,
          QRLink,
          newOrder
        );
        this.goToPaymentSuccess(this.paymentIntent.uuid);
      };

      if (orderFailed) {
        this.stopOrderSyncFallback();
        this.posStatus = PAYMENT_STATUSES.error;
        this.$refs?.status?.hide();
        this.$refs?.errorBottomSheet?.open('ORDER_CREATE_FAILED');
        this.printReceipt(this.cardholderReceipt);
        this.voidPayment();
        return;
      } else if (sendingToPOS && orderSentToPOS) {
        this.stopOrderSyncFallback();
        onCreateOrderSuccess();
      } else if (sendingToPOS) {
        this.dialogText = [this.$t('kiosk.payInPerson.terminal.sendingToPOS')];
      } else if (!integratedToPOS && newOrder.payments.length > 0) {
        this.stopOrderSyncFallback();
        onCreateOrderSuccess();
      }
    },
    // the background void sweep must not seize the terminal mid-sale
    posStatus(status) {
      this.$store.dispatch('setSaleInProgress', {
        inProgress: status === PAYMENT_STATUSES.processing,
      });
    },
    async terminalOnline(newStatus, oldStatus) {
      if (newStatus && !oldStatus) {
        if (this.terminalOfflineTimeoutId) {
          clearTimeout(this.terminalOfflineTimeoutId);
          this.terminalOfflineTimeoutId = null;
        }
        await this.reconnectToCardReader();
        if (
          this.hasRetriedNP &&
          this.posStatus !== PAYMENT_STATUSES.processing
        ) {
          this.posStatus = PAYMENT_STATUSES.idle;
          this.pay({ isNPRetry: true });
          return;
        }
      } else if (!newStatus) {
        if (this.terminalOfflineTimeoutId) {
          clearTimeout(this.terminalOfflineTimeoutId);
        }
        this.terminalOfflineTimeoutId = setTimeout(() => {
          this.terminalOfflineTimeoutId = null;
          if (this.hasRetriedNP) {
            return;
          }
          this.posStatus = PAYMENT_STATUSES.idle;
          this.$refs?.status?.hide();
          this.$refs?.errorBottomSheet?.open('NP');
        }, 2000);
      }
    },
  },
  beforeDestroy() {
    this.$store.dispatch('setSaleInProgress', { inProgress: false });
    if (this.terminalOfflineTimeoutId) {
      clearTimeout(this.terminalOfflineTimeoutId);
    }
    if (this.abortResetTimeoutId) {
      clearTimeout(this.abortResetTimeoutId);
    }
    this.clearProcessingWatchdog();
    this.stopOrderSyncFallback();
    window.removeEventListener('online', this.handleNetworkReconnect);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityReconnect
    );
  },
  async mounted() {
    window.addEventListener('online', this.handleNetworkReconnect);
    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityReconnect
    );

    await this.initialize();
  },
  methods: {
    async initialize() {
      this.loading = true;
      const sessionToken = this.$route.params.session;

      try {
        await this.$store.dispatch('fetchSessionData', {
          session: sessionToken,
        });
        await this.$store.dispatch('subscribeToPosTerminal');
        this.setTerminalMessageCallback();
        await this.ensureOrderEventsAreConnected();
      } catch (error) {
        console.error('PAY_IN_PERSON: initialization failed', error);
        this.initialized = false;
        this.loading = false;
        await this.$nextTick();
        this.$refs?.errorBottomSheet?.open('GENERIC');
        return;
      }

      this.initialized = true;

      this.$nextTick(() => {
        this.pay();
        const restaurantId = this.$store.getters.getCurrentRestaurantId;
        if (`${restaurantId}` === '1527') {
          this.$store.dispatch('sendAnalyticsEvent', {
            event: 'app_pay-in-person_start_payment',
          });
        }
      });
    },
    async handleRetry() {
      if (!this.initialized) {
        await this.initialize();
        return;
      }
      await this.pay();
    },
    goBack() {
      this.handleAbortTransaction();
      this.$router.go(-1);
    },
    async startNewSession() {
      this.resetting = true;
      try {
        await this.$store.dispatch('generateNewKioskSession');
      } catch (error) {
        console.error('PAY_IN_PERSON: failed to start a new session', error);
        this.resetting = false;
      }
    },
    async pay({ isNPRetry = false } = {}) {
      if (this.posStatus === PAYMENT_STATUSES.processing) {
        return;
      }

      if (!isNPRetry) {
        this.hasRetriedNP = false;
      }

      try {
        await this.generatePaymentIntent(false);
      } catch (error) {
        console.error(
          'PAY_IN_PERSON: failed to generate payment intent',
          error
        );
        this.loading = false;
        await this.$nextTick();
        this.$refs?.status?.hide();
        this.$refs?.errorBottomSheet?.open('GENERIC');
        return;
      }
      this.loading = false;
      this.showResetButton = false;
      this.abortedByUser = false;

      await this.$nextTick();

      this.posStatus = PAYMENT_STATUSES.processing;
      this.startProcessingWatchdog(SALE_WATCHDOG_MS);
      this.startOrderSyncFallback();
      this.dialogText = [
        this.$t('kiosk.payInPerson.terminal.followInstructions'),
      ];
      this.$refs.status.show({
        status: 'loading',
        showCancel: true,
      });

      const CRN = this.paymentIntent.CRN;
      const data = {
        ecrNameVersion: this.paymentIntent.ecr_name_version,
        receiptLineLength: this.paymentIntent.receipt_line_length,
        CRN,
        dlgDisabled: false,
        operation: OPERATION.SALE,
        amount: Math.round(this.paymentIntent.total * 100),
      };

      const onError = () => {
        this.stopOrderSyncFallback();
        this.posStatus = PAYMENT_STATUSES.idle;
        this.$refs?.status?.hide();
        this.$refs?.errorBottomSheet?.open('GENERIC');
      };

      this.$store.dispatch('addUnresolvedCRN', {
        CRN,
        orderNumber: this.$route.params.order,
      });
      try {
        await this.$store.dispatch('sendPosTerminalMessage', { data, onError });
      } catch (error) {
        console.error('PAY_IN_PERSON: failed to send SALE to terminal', error);
        onError();
      }
    },
    goToPaymentSuccess(paymentUUID = null) {
      this.$router.push({
        name: 'PayInPersonSuccess',
        query: {
          orderNumber: this.$route.params.order,
        },
        params: {
          session: this.$store.state.session,
          paymentUUID,
        },
      });
    },
    async handleCompleteSale(data) {
      // if transaction was aborted by user from our side after correct PIN entry.
      if (this.abortedByUser) {
        console.warn('Transaction was aborted by user. Voiding payment.');
        // this.dialogText = [];
        this.printReceipt(data.cardholderReceipt);
        setTimeout(() => {
          this.voidPayment(data.CRN);
          this.stopOrderSyncFallback();
          this.posStatus = PAYMENT_STATUSES.idle;
          this.$refs?.status?.hide();
          this.abortedByUser = false;
          this.$refs?.errorBottomSheet?.open('UC');
        }, 2000);
        return;
      }

      this.posStatus = PAYMENT_STATUSES.processing;
      // from here on we only wait on our own backend, so tighten the watchdog
      this.startProcessingWatchdog(COMPLETING_ORDER_WATCHDOG_MS);
      this.$refs?.status?.show({
        status: 'loadingGeneric',
        showCancel: false,
      });
      this.dialogText = [this.$t('kiosk.payInPerson.terminal.completingOrder')];

      this.cardholderReceipt = data.cardholderReceipt;

      try {
        await this.$store.dispatch('updateInPersonPaymentIntent', {
          type: 'payment_succeeded',
          data,
        });
      } catch (error) {
        // the card was already charged — do NOT offer a retry that would
        // re-initiate the sale; the order sync fallback keeps polling and the
        // processing watchdog surfaces the reset button if nothing resolves.
        // The CRN stays in the unresolved queue, so a charge whose order
        // never materializes is reversed by the background sweep.
        console.error(
          'PAY_IN_PERSON: failed to report successful payment',
          error
        );
        this.dialogText = [
          this.$t('kiosk.payInPerson.terminal.completingOrderDelayed'),
        ];
        Sentry.captureMessage(
          'PAY_IN_PERSON: payment_succeeded callback failed after charge',
          {
            level: 'error',
            extra: {
              CRN: this.paymentIntent.CRN,
              orderNumber: this.$route.params.order,
            },
          }
        );
      }
      // the CRN deliberately stays in the unresolved queue here: the charge
      // is only settled once the order exists (onCreateOrderSuccess) or the
      // payment is voided
    },
    voidPayment(CRN = this.paymentIntent.CRN) {
      const data = {
        CRN,
        operation: OPERATION.VOID,
        reversalType: 'autoVoid',
        dlgDisabled: false,
        receiptLineLength: this.paymentIntent.receipt_line_length,
      };

      this.$store.dispatch('sendPosTerminalMessage', { data });
    },
    async handleForceSettle() {
      this.stopOrderSyncFallback();
      this.$store.dispatch('removeUnresolvedCRN', {
        CRN: this.paymentIntent.CRN,
      });
      this.posStatus = PAYMENT_STATUSES.processing;
      this.$refs.status.show({
        status: 'loadingGeneric',
        showCancel: false,
      });
      this.dialogText = [this.$t('kiosk.payInPerson.terminal.forceSettle')];
      const CRN = generateCRN();

      // wait for a while to ensure the terminal is ready to accept new commands
      return new Promise(resolve => {
        setTimeout(() => {
          const data = {
            operation: OPERATION.SETTLEMENT,
            CRN,
            ecrNameVersion: this.paymentIntent.ecr_name_version,
            receiptLineLength: this.paymentIntent.receipt_line_length,
            dlgDisabled: false,
          };

          this.$store.dispatch('sendPosTerminalMessage', { data });
          this.posStatus = PAYMENT_STATUSES.idle;
          resolve();
        }, 2000);
      });
    },
    handleAbortTransaction() {
      this.stopOrderSyncFallback();
      this.abortedByUser = true;
      // this.dialogText = [];

      const CRN = this.paymentIntent.CRN;
      const data = { CRN, operation: OPERATION.ABORT };

      this.$store.dispatch('sendPosTerminalMessage', { data });
      this.$refs.status.show({
        status: 'loadingGeneric',
        showCancel: false,
        CRN,
      });

      // if it takes too long to abort the transaction, show the reset button
      if (this.abortResetTimeoutId) {
        clearTimeout(this.abortResetTimeoutId);
      }
      this.abortResetTimeoutId = setTimeout(() => {
        this.abortResetTimeoutId = null;
        const stillOpen = this.$refs?.status?.getIsOpenForCRN(CRN);
        if (stillOpen) {
          this.showResetButton = true;
        }
      }, ABORT_RESET_BUTTON_MS);
    },
    setTerminalMessageCallback() {
      const onmessage = async message => {
        let data;
        try {
          data = JSON.parse(message.data);
        } catch (error) {
          console.error('TERMINAL: failed to parse message', message.data);
          return;
        }
        this.error = null;

        if (data.operation === OPERATION.DIALOG) {
          console.log('TERMINAL: Dialog message received', data);
        } else if (data.result && data.operation === OPERATION.SALE) {
          await this.handleCompleteSale(data);
        } else if (
          !data.result &&
          data.responseCode === 'SR' &&
          data.operation === OPERATION.SALE
        ) {
          await this.handleForceSettle();
          setTimeout(() => {
            this.pay();
          }, 1000);
          return;
        } else if (data.result && data.operation === OPERATION.SETTLEMENT) {
          this.printReceipt(data.merchantReceipt);
          this.$store.dispatch('saveSettlement', {
            data,
          });
          this.$store.dispatch('removeUnresolvedCRN', { CRN: data.CRN });

          try {
            const order = this.$route.params.order;
            this.paymentIntent = await this.$store.dispatch(
              'generateInPersonPaymentIntent',
              { order }
            );
          } catch (error) {
            // keep the previous intent rather than clobbering it with nothing
            console.error(
              'PAY_IN_PERSON: failed to refresh payment intent after settlement',
              error
            );
          }
          this.$refs?.status?.hide();
          return;
        } else if (data.operation === OPERATION.VOID && data.result) {
          this.stopOrderSyncFallback();
          this.posStatus = PAYMENT_STATUSES.void;
          this.dialogText = [this.$t('kiosk.payInPerson.terminal.voidSuccess')];
          this.printReceipt(data.cardholderReceipt);

          this.$store.dispatch('removeUnresolvedCRN', { CRN: data.CRN });

          try {
            await this.$store.dispatch('updateInPersonPaymentIntent', {
              type: 'payment_voided',
              data,
            });
          } catch (error) {
            // the terminal already reversed the charge; losing this report
            // must not break the screen, but it must be visible off-kiosk
            console.error(
              'PAY_IN_PERSON: failed to report the void to the backend',
              error
            );
            Sentry.captureMessage(
              'PAY_IN_PERSON: payment_voided callback failed',
              {
                level: 'error',
                extra: { CRN: data.CRN },
              }
            );
          }

          try {
            const order = this.$route.params.order;
            this.paymentIntent = await this.$store.dispatch(
              'generateInPersonPaymentIntent',
              { order }
            );
          } catch (error) {
            // keep the previous intent rather than clobbering it with nothing
            console.error(
              'PAY_IN_PERSON: failed to refresh payment intent after void',
              error
            );
          }
          return;
        } else if (
          data.operation === OPERATION.VOID &&
          !data.result &&
          data.responseCode === 'NF'
        ) {
          console.error('TERMINAL: CRN was not found, nothing to auto-void.');
          this.$store.dispatch('removeUnresolvedCRN', { CRN: data.CRN });
        } else if (
          !data.result &&
          data.responseCode === 'NP' &&
          this.posStatus === PAYMENT_STATUSES.processing &&
          !this.hasRetriedNP
        ) {
          console.log('WORLDLINE: SALE returned NP, retrying silently...');
          this.hasRetriedNP = true;
          this.$store.dispatch('removeUnresolvedCRN', {
            CRN: this.paymentIntent.CRN,
          });
          setTimeout(() => {
            this.posStatus = PAYMENT_STATUSES.idle;
            this.pay({ isNPRetry: true });
          }, 1500);
          return;
        } else if (!data.result) {
          this.stopOrderSyncFallback();
          const CRN = this.paymentIntent.CRN;
          this.error = data.responseText;
          this.posStatus = PAYMENT_STATUSES.error;
          this.$refs?.status?.hide();

          // mute 1xx error codes for now
          // const match1xxError = data.responseCode.match(/1\d{2}/); // 1xx error codes
          // if (match1xxError) {
          //   this.printReceipt(
          //     data.cardholderReceipt || data.merchantReceipt || CRN
          //   );
          // }

          if (data.cardholderReceipt) {
            this.printReceipt(data.cardholderReceipt);
          }

          this.$refs.errorBottomSheet?.open(
            data.responseCode,
            CRN,
            data.responseText
          );
        } else {
          this.dialogText = [
            this.$t('kiosk.payInPerson.terminal.unknownError'),
          ];
          this.posStatus = PAYMENT_STATUSES.idle;
          this.$refs?.status?.hide();
        }
      };
      this.$store.dispatch('setOnMessageCallback', { callback: onmessage });
    },
    async generatePaymentIntent(withLoader = true) {
      const order = this.$route.params.order;
      if (withLoader) {
        this.loading = true;
      }
      const paymentIntent = await this.$store.dispatch(
        'generateInPersonPaymentIntent',
        { order }
      );

      this.paymentIntent = paymentIntent;
      this.paymentIntent.receipt_line_length =
        paymentIntent.receipt_line_length || 40;
      this.loading = false;
    },
    printReceipt(text, orderId = null, additionalFields, QRLink, order = null) {
      this.$store.dispatch('printReceipt', {
        text,
        orderId,
        additionalFields,
        QRLink,
        items: order?.items || [],
        discount: order?.discount,
        discountCode: order?.coupon,
      });
    },
    async reconnectToCardReader() {
      await this.$store.dispatch('subscribeToPosTerminal', { force: true });
      this.setTerminalMessageCallback();
    },
    async ensureOrderEventsAreConnected({ forceResubscribe = false } = {}) {
      if (forceResubscribe) {
        await this.$store.dispatch('subscribeToPusherChannels');
      }

      await this.$store.dispatch('fetchOrders');
      await this.$store.dispatch('subscribeToPusherChannels');
    },
    async syncOrdersWithFallback({ withResubscribe = false } = {}) {
      if (this.isSyncingOrders) {
        return;
      }

      this.isSyncingOrders = true;
      try {
        await this.$store.dispatch('fetchOrders');
        if (withResubscribe) {
          await this.$store.dispatch('subscribeToPusherChannels');
        }
      } catch (error) {
        console.warn('PAY_IN_PERSON: fallback order sync failed', error);
      } finally {
        this.isSyncingOrders = false;
      }
    },

    startOrderSyncFallback() {
      this.stopOrderSyncFallback();

      this.orderSyncIntervalId = setInterval(() => {
        this.syncOrdersWithFallback();
      }, ORDER_SYNC_INTERVAL_MS);
    },
    stopOrderSyncFallback() {
      if (!this.orderSyncIntervalId) {
        return;
      }

      clearInterval(this.orderSyncIntervalId);
      this.orderSyncIntervalId = null;
    },
    startProcessingWatchdog(timeoutMs) {
      this.clearProcessingWatchdog();
      this.processingWatchdogId = setTimeout(() => {
        this.processingWatchdogId = null;
        // only fires if we are still stuck in processing — every terminal
        // response path moves posStatus away from it or navigates away
        if (this.posStatus === PAYMENT_STATUSES.processing) {
          console.warn('PAY_IN_PERSON: processing watchdog fired');
          this.showResetButton = true;
        }
      }, timeoutMs);
    },
    clearProcessingWatchdog() {
      if (!this.processingWatchdogId) {
        return;
      }

      clearTimeout(this.processingWatchdogId);
      this.processingWatchdogId = null;
    },
    async handleNetworkReconnect() {
      await this.syncOrdersWithFallback({ withResubscribe: true });
    },
    async handleVisibilityReconnect() {
      if (document.visibilityState !== 'visible') {
        return;
      }

      await this.syncOrdersWithFallback({ withResubscribe: true });
    },
  },
};
</script>

<style lang="scss" scoped>
.pay-container {
  min-height: 100vh;
  justify-content: center;
  align-items: center;
  display: flex;
  background-size: 400% 400%;
}

.pay-content-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: white;
  border-radius: 10px;
  width: 100%;
  margin-left: 40px;
  margin-right: 40px;
  margin-bottom: 40px;
  padding: 40px;
}

.icon-container {
  margin-top: -100px;
}

.icon-content-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background: black;
  width: 120px;
  height: 120px;
  border-radius: 50%;
}

.pay-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
}
</style>
